import { Component, OnInit, ViewChild, afterNextRender, DestroyRef, NgZone } from '@angular/core';
import { LeftUiComponent } from '../left-ui/left-ui/left-ui.component';
import { CommonModule } from '@angular/common';
import { FormsModule, NgForm } from '@angular/forms';
import { ToastrService } from 'ngx-toastr';
import { debounceTime } from 'rxjs';
import { Router, RouterModule } from '@angular/router';
import { AccountService } from '../../Utils/_services/account/account.service';
import { IRegister_Request } from '../../Utils/_services/account/account.model';
import { ICustomError } from '../../Utils/_models/network-response';

@Component({
  selector: 'app-register',
  standalone: true,
  imports: [FormsModule, CommonModule, RouterModule, LeftUiComponent],
  templateUrl: './register.component.html',
  styleUrl: './register.component.css'
})
export class RegisterComponent implements OnInit {
  @ViewChild('registerForm') registerForm?: NgForm;
  model: any = {};
  private _destroyRef: DestroyRef;
  isLoading: boolean = false;
  isSubmitting: boolean = false;
  showPassword = false;
  showConfirmPassword = false;
  minDate: string = '';
  maxDate: string = '';

  constructor(
    private accountService: AccountService,
    private toastr: ToastrService,
    destroyRef: DestroyRef,
    private router: Router,
    private ngZone: NgZone
  ) {
    this._destroyRef = destroyRef;
  }

  ngOnInit(): void {
    const today = new Date();
    
    // Max Date: Must be at least 18 years old
    const maxDateObj = new Date(today.getFullYear() - 18, today.getMonth(), today.getDate());
    this.maxDate = maxDateObj.toISOString().split('T')[0];

    // Min Date: Maximum age of 120 years (prevent accidental impossible dates)
    const minDateObj = new Date(today.getFullYear() - 120, today.getMonth(), today.getDate());
    this.minDate = minDateObj.toISOString().split('T')[0];

    // afterNextRender(() => {
      const savedForm = window.localStorage.getItem("saved-register-form");
      if (savedForm) {
        this.model = JSON.parse(savedForm);
      }

      const subscription = this.registerForm?.valueChanges?.pipe(
        debounceTime(500)
      ).subscribe(value => {
        window.localStorage.setItem("saved-register-form", JSON.stringify(value));
      });

      if (subscription) {
        this._destroyRef.onDestroy(() => subscription.unsubscribe());
      }
    // });
  }

  get hasMinLength(): boolean {
    return this.model.password ? this.model.password.length >= 8 : false;
  }
  
  get hasUppercase(): boolean {
    return this.model.password ? /[A-Z]/.test(this.model.password) : false;
  }

  get hasNumber(): boolean {
    return this.model.password ? /\d/.test(this.model.password) : false;
  }

  get hasSpecialChar(): boolean {
    return this.model.password ? /[!@#$%^&*(),.?":{}|<>]/.test(this.model.password) : false;
  }

  get passwordStrength(): number {
    if (!this.model.password) return 0;
    let strength = 0;
    if (this.hasMinLength) strength++;
    if (this.hasUppercase) strength++;
    if (this.hasNumber) strength++;
    if (this.hasSpecialChar) strength++;
    return strength;
  }

  getStrengthColor(): string {
    const s = this.passwordStrength;
    if (s === 0) return '#a8462f';
    if (s === 1) return '#a8462f';
    if (s === 2) return '#c9873c';
    if (s === 3) return '#c9873c';
    return '#2f6b56';
  }

  getStrengthLabel(): string {
    const s = this.passwordStrength;
    if (!this.model.password) return 'Use 8+ characters with a number, an uppercase letter, and a symbol.';
    const labels = [
      'Use 8+ characters with a number, an uppercase letter, and a symbol.',
      'Weak — add a number, a capital letter, or a symbol.',
      'Getting there — add one more type of character.',
      'Almost — one more requirement to go.',
      'Strong password.'
    ];
    return labels[s];
  }

  get passwordsMatch(): boolean {
    return this.model.password && this.model.confirmPassword && this.model.password === this.model.confirmPassword;
  }

  togglePasswordVisibility(): void {
    this.showPassword = !this.showPassword;
  }

  toggleConfirmPasswordVisibility(): void {
    this.showConfirmPassword = !this.showConfirmPassword;
  }

  onSubmitForm(): void {
    if (!this.model.tos || !this.model.privacy) {
      this.toastr.error("Please agree to the Terms of Service and Privacy Policy");
      return;
    }
    if (this.registerForm?.invalid || this.passwordStrength < 4 || !this.passwordsMatch) {
      this.toastr.error("Please fill all required fields correctly and ensure passwords match.", "Validation Error");
      this.registerForm?.form.markAllAsTouched();
      return;
    }

    if (this.isSubmitting) {
      return;
    }

    this.isSubmitting = true;
    this.isLoading = true;

    const registerRequest: IRegister_Request = {
      firstName: null,
      lastName: null,
      email: this.model.email,
      password: this.model.password,
      confirmPassword: this.model.confirmPassword,
      dob: "2000-01-01",
      newsletter: this.model.newsletter || false
    };

    this.accountService.registerUser(registerRequest).subscribe({
      next: (response: any) => {
        this.isSubmitting = false;
        this.isLoading = false;

        this.registerForm?.reset();
        window.localStorage.removeItem("saved-register-form");
        this.model = {};

        const data = response?.data;
        const token = response?.auth?.token;
        
        if (token && data) {
           const userLogin = {
             firstName: data.firstName,
             email: data.email,
             username: data.username,
             token: token,
             role: data.role
           };
           localStorage.setItem("user", JSON.stringify(userLogin));
           this.accountService.setCurrentUser(userLogin);
        }

        if (data && data.registrationStep !== undefined && !data.registrationCompleted) {
          if (data.registrationStep > 1) {
             this.toastr.info("Continuing your registration...", "Welcome Back");
             this.ngZone.run(() => {
                // Determine step routing based on registrationStep
                if (data.registrationStep === 2) {
                   this.router.navigate(['/auth/phone-verification']);
                } else if (data.registrationStep === 3) {
                   this.router.navigate(['/auth/user-details']);
                } else {
                   this.router.navigate(['/user/dashboard']);
                }
             });
             return;
          }
        }

        this.toastr.success(
          `Welcome! A verification link has been sent to your email. Please check your inbox.`,
          "Registration Successful"
        );

        this.ngZone.run(() => {
          this.router.navigate(['/auth/email-verification'], { state: { email: registerRequest.email } });
        });
      },
      error: (error: ICustomError) => {
        this.isSubmitting = false;
        this.isLoading = false;

        let errorMessage = "Registration failed. Please try again.";

        if (error?.error?.msg) {
          errorMessage = error.error.msg;
        } else if (error?.message) {
          errorMessage = error.message;
        }

        this.toastr.error(errorMessage, "Registration Failed");
        console.error('Registration error:', error);
      }
    });
  }

  cancel(): void {
    console.log("Registration process finished or cancelled.");
  }
}
